/**
 * The product's read model: the backend's records, in the shape the screens read.
 *
 * The screens in `src/v07/screens/` are the product. They were composed against the approved
 * prototype's own records, so they read one object graph — templates carrying releases and a
 * draft, credentials carrying their release, their subject snapshot, their manifest and their
 * events. This module builds exactly that graph from the real API instead of from a seed, so the
 * same screen renders the same way whether it is showing the visual-acceptance fixture or a live
 * database.
 *
 * Three rules this mapping keeps:
 *
 *   1. NOTHING IS INVENTED. Where the backend has no answer for a field the prototype's model
 *      carries, the field takes its empty value and the reason is written down, in a `GAP:` note
 *      naming what would have to exist. A plausible-looking placeholder would be worse than an
 *      empty cell, because a reviewer cannot tell the two apart.
 *
 *   2. THE CONTRACT'S FREE OBJECTS CARRY THE PROTOTYPE'S MODEL. `TemplateDraft.layout`,
 *      `LayerDefinition.placement` and `LayerDefinition.style` are free-form by contract, and the
 *      publish gate snapshots them verbatim into the immutable release payload. The prototype's
 *      richer template model — canvas geometry, background placement, validity policy, localized
 *      metadata, e-mail defaults, interoperability, per-field presentation — therefore rides
 *      inside them, under one namespaced key, and becomes immutable with the release exactly as
 *      the rest of the design does. No migration, no new column, and no field whose meaning the
 *      backend would have to guess.
 *
 *   3. THE BACKEND STAYS AUTHORITATIVE. Anything the backend validates or enforces — field keys,
 *      data types, required-ness, visibility, the holder invariant, the publication policy, the
 *      duplicate policy, the permissions — is written to its OWN columns and read back from them.
 *      The namespaced block carries presentation, never a rule.
 *
 * `to-api.ts` is the same mapping in the other direction.
 */

import type { ApiClient } from '../../api/client';
import { isApiError } from '../../api/errors';
import {
  listEmailTemplates,
  listTemplates,
  readCredential,
  readCredentialDeliveries,
  readCredentialEvents,
  readIntegrationSettings,
  readIssuerProfiles,
  readRelease,
  readTemplate,
} from '../../api/internal';
import { getPublicArtifactSvg } from '../../api/publicApi';
import type {
  CredentialDetail as ApiCredentialDetail,
  CredentialEventRecord,
  CredentialListRow,
  EmailTemplateRecord,
  FieldDefinition,
  IssuanceBatchListSummary,
  IssuanceBatchRecord,
  IssuanceRowWithCredential,
  IssuerProfile,
  ReleaseDetail,
  TemplateDetail,
  TemplateReleaseRecord,
} from '../../api/types';
import { latestDeliveryStatus } from '../lib/delivery-status';
import { organizationIdFromReference } from '../lib/linkedin-add-to-profile';
import {
  type LinkedInIssuingOrganization,
  readLinkedInIssuingOrganization,
  type StoredIssuingOrganization,
} from '../lib/linkedin-issuing-organization';
import { layerFromApi as mapLayerFromApi } from './layer-geometry';
import type {
  BatchSummary,
  CredentialState,
  DemoBatch,
  DemoBatchRow,
  DemoCredential,
  DemoDatabase,
  DemoDelivery,
  DemoDraft,
  DemoEvent,
  DemoFieldDefinition,
  DemoRelease,
  DemoSettings,
  DemoTemplate,
  SubjectType,
} from './types';

/**
 * The one key the prototype's template model lives under, inside the contract's free `layout`.
 *
 * Namespaced so it can never collide with the backend's own `canvas` / `output` entries, and so a
 * reader of a stored draft can tell at a glance which half of the object is whose.
 */
export const V07_LAYOUT_KEY = 'v07';

/* The layer translation lives in one module; re-exported here so existing callers keep their import. */
export { layerFromApi } from './layer-geometry';

export interface V07TemplateModel {
  credentialType: string;
  customTypeLabel: string | null;
  subjectType: SubjectType;
  holderField: string;
  defaultLanguage: 'tr' | 'en';
  localizedMetadata: Record<string, Record<string, string>>;
  validityPolicy: Record<string, unknown>;
  emailDefaults: Record<string, unknown>;
  interoperability: Record<string, unknown>;
  canvas: Record<string, unknown>;
  background: Record<string, unknown> | null;
  /** The artwork each sealed language draws on; see `DemoDraft.localizedBackground`. */
  localizedBackground: Record<string, Record<string, unknown> | null>;
  /** The languages this template seals a document in; see `DemoDraft.sealedLanguages`. */
  sealedLanguages: ('tr' | 'en')[];
  safeAreas: unknown[];
  /** Per-field presentation the backend has no column for; keyed by the field's own key. */
  fieldMeta: Record<string, Record<string, unknown>>;
  /**
   * The organisation this template names in LinkedIn's «Veren kuruluş» field, when it sets one.
   *
   * Optional and normally null. It changes nothing else — not the issuer of record, not the
   * verification page, not the drawing — and it is frozen into each release with the rest of the
   * model, so editing it later leaves already-issued documents alone.
   */
  linkedInIssuer: LinkedInIssuingOrganization | null;
}

const asObject = (v: unknown): Record<string, unknown> =>
  v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};

const asArray = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);

/** The prototype's own defaults, used only when a template carries no stored model yet. */
export function emptyTemplateModel(): V07TemplateModel {
  return {
    credentialType: 'badge',
    customTypeLabel: null,
    subjectType: 'person',
    holderField: 'recipient_name',
    defaultLanguage: 'tr',
    localizedMetadata: {},
    validityPolicy: { mode: 'none' },
    emailDefaults: {},
    interoperability: {},
    canvas: { width: 1080, height: 1080, aspectRatio: 1, orientation: 'square', unit: 'px', outputScale: 1 },
    background: null,
    localizedBackground: {},
    sealedLanguages: [],
    safeAreas: [],
    fieldMeta: {},
    linkedInIssuer: null,
  };
}

/**
 * What the stored model says about the LinkedIn organisation — absent, usable, or broken.
 *
 * Deliberately not part of `V07TemplateModel`: that type is also what `templateModelToLayout`
 * WRITES, and the three-state answer is a reading of the stored block, not a shape to store.
 */
export function storedLinkedInIssuer(
  layout: Record<string, unknown> | null | undefined,
): StoredIssuingOrganization {
  return readLinkedInIssuingOrganization(asObject(asObject(layout ?? {})[V07_LAYOUT_KEY]));
}

export function readTemplateModel(layout: Record<string, unknown> | null | undefined): V07TemplateModel {
  const stored = asObject(asObject(layout ?? {})[V07_LAYOUT_KEY]);
  const base = emptyTemplateModel();
  return {
    credentialType: typeof stored.credentialType === 'string' ? stored.credentialType : base.credentialType,
    customTypeLabel: typeof stored.customTypeLabel === 'string' ? (stored.customTypeLabel as string) : null,
    subjectType: stored.subjectType === 'organization' ? 'organization' : 'person',
    holderField: typeof stored.holderField === 'string' ? stored.holderField : base.holderField,
    defaultLanguage: stored.defaultLanguage === 'en' ? 'en' : 'tr',
    localizedMetadata: asObject(stored.localizedMetadata) as Record<string, Record<string, string>>,
    validityPolicy: Object.keys(asObject(stored.validityPolicy)).length
      ? asObject(stored.validityPolicy)
      : base.validityPolicy,
    emailDefaults: asObject(stored.emailDefaults),
    interoperability: asObject(stored.interoperability),
    canvas: Object.keys(asObject(stored.canvas)).length ? asObject(stored.canvas) : base.canvas,
    background:
      stored.background === null || stored.background === undefined ? null : asObject(stored.background),
    /* Read defensively and per language: a stray entry in one template's opaque blob must not
       make the whole model unreadable, so anything that is not an object comes back as nothing. */
    localizedBackground: Object.fromEntries(
      (['tr', 'en'] as const)
        .map((l) => [l, asObject(stored.localizedBackground)[l]] as const)
        .filter(([, v]) => v !== null && typeof v === 'object' && !Array.isArray(v))
        .map(([l, v]) => [l, asObject(v)]),
    ),
    /* Unsupported entries are dropped rather than fatal, and the order is the product's own. */
    sealedLanguages: (['tr', 'en'] as const).filter((l) => asArray(stored.sealedLanguages).includes(l)),
    safeAreas: asArray(stored.safeAreas),
    fieldMeta: asObject(stored.fieldMeta) as Record<string, Record<string, unknown>>,
    /* The editable value, re-validated rather than trusted. A stored block that cannot be read
       comes back as nothing here so the author can re-enter it; whether it was ABSENT or BROKEN
       is a different question, and `storedLinkedInIssuer` below is the one that answers it. */
    linkedInIssuer: (() => {
      const read = readLinkedInIssuingOrganization(stored);
      return read.status === 'custom' ? read.value : null;
    })(),
  };
}

// ---------------------------------------------------------------------------- fields and layers

/**
 * The prototype's four-valued field visibility, over the backend's two columns.
 *
 * `visibility` and `publicVisible` are what the backend enforces — the public read model drops a
 * field unless both say so — and they stay authoritative. Which of the two PUBLIC values a field
 * has (metadata only, or metadata and render) is a presentation distinction the backend does not
 * need and does not store, so it round-trips through the field's own meta.
 */
export function fieldVisibilityFromApi(
  f: Pick<FieldDefinition, 'visibility' | 'publicVisible'>,
  meta: Record<string, unknown>,
): DemoFieldDefinition['visibility'] {
  const stored = meta.visibility;
  if (
    stored === 'private' ||
    stored === 'public_metadata' ||
    stored === 'public_render' ||
    stored === 'public_metadata_and_render'
  ) {
    return stored;
  }
  if (f.visibility !== 'public') return 'private';
  return f.publicVisible ? 'public_metadata_and_render' : 'public_render';
}

export function fieldFromApi(f: FieldDefinition, model: V07TemplateModel): DemoFieldDefinition {
  const meta = asObject(model.fieldMeta[f.key]);
  const labels = asObject(meta.labels) as { tr?: string; en?: string };
  return {
    /* The prototype keys a field by `id`; the backend keys it by `key`, which is the stable one. */
    id: typeof meta.id === 'string' ? (meta.id as string) : f.key,
    key: f.key,
    labels: Object.keys(labels).length > 0 ? labels : { tr: f.label },
    /* `qr` and `credential_url` are written to the backend as `url`, which is the member its enum
       has; the prototype's own word comes back from `fieldMeta` (`toApi`'s `API_DATA_TYPE`). */
    dataType: typeof meta.dataType === 'string' ? (meta.dataType as string) : f.dataType,
    required: f.required,
    importable: meta.importable !== false,
    visibility: fieldVisibilityFromApi(f, meta),
    defaultValue: f.defaultValue ?? '',
    normalization: asArray(meta.normalization) as string[],
    /*
     * The enum's own options live in the backend's `enumOptions` COLUMN, not inside `validation` —
     * it validates them there, and the publish gate snapshots them there. The prototype's
     * validator reads `validation.enum`. Without this line the two never meet: every enum field
     * comes back with no options, and every value offered for it is refused with «seçenek listesi
     * tanımlı değil» — which made batch import of any template carrying an enum produce zero
     * valid rows while insisting the file was at fault.
     */
    validation: {
      ...asObject(f.validation),
      ...(f.enumOptions && f.enumOptions.length > 0 ? { enum: [...f.enumOptions] } : {}),
    },
    sample: typeof meta.sample === 'string' ? (meta.sample as string) : '',
    auto: meta.auto === true,
    role: f.holderRole,
    custom: meta.custom === true,
  };
}

/**
 * A layer, back out of the contract's own free objects.
 *
 * `placement` and `style` are free-form by contract and the publish gate snapshots them verbatim,
 * so the prototype's layer — its normalised coordinates, its font size ratio, its alignment, its
 * colour — is stored whole and read back whole. `layerKey`, `layerType`, `fieldKey`, `assetId`,
 * `assetSha256`, `fontFamily` and `zIndex` stay in the backend's own columns, because those are
 * the ones it validates (a text layer must bind a field, an asset layer must name a known digest).
 */

// ---------------------------------------------------------------------------- releases and drafts

function issuerSnapshot(issuer: IssuerProfile | null): DemoRelease['issuerProfileSnapshot'] {
  return {
    displayName: issuer?.issuerDisplayName ?? '',
    linkedinIssuerName: issuer?.linkedinIssuerName ?? '',
    linkedinPageUrl: issuer?.linkedinPageUrl ?? '',
    linkedinOrganizationId: organizationIdFromReference(issuer?.organizationReference ?? null),
    website: issuer?.issuerWebsite ?? '',
    supportEmail: issuer?.supportEmail ?? '',
    publicBase: issuer?.publicBase ?? '',
  };
}

/**
 * A published release, from its immutable payload.
 *
 * GAP: the release payload does not snapshot the ISSUER PROFILE. The prototype's release carries
 * `issuerProfileSnapshot` so a credential issued years ago still names the issuer as it was then;
 * the backend resolves the issuer live, through the template. Until the payload carries it, this
 * shows the CURRENT issuer profile — which is right for every existing record and wrong only for
 * one whose issuer has since been renamed. Closing it means adding the snapshot at publish time,
 * which changes `releaseHash` for new releases and therefore needs a Product Owner decision.
 */
export function releaseFromApi(
  release: TemplateReleaseRecord,
  detail: ReleaseDetail | null,
  template: { code: string; name: string },
  issuer: IssuerProfile | null,
): DemoRelease {
  const payload = asObject(release.payload);
  const model = readTemplateModel(asObject(payload.layout));
  const storedIssuer = storedLinkedInIssuer(asObject(payload.layout));
  const permissions = asObject(payload.permissions);
  const fields = (detail?.fields ?? []).map((f) => fieldFromApi(f, model));
  const layers = (detail?.layers ?? []).map((l) => mapLayerFromApi(l, model.canvas as never));
  return {
    id: release.id,
    templateId: release.templateId,
    templateCode: template.code,
    templateName: template.name,
    releaseNumber: release.releaseNumber,
    releaseSchemaVersion: release.releaseSchemaVersion,
    releasedAt: release.publishedAt,
    releaseNote: null,
    releaseHash: release.releaseHash,
    credentialType: model.credentialType,
    customTypeLabel: model.customTypeLabel,
    subjectType: model.subjectType,
    holderField: model.holderField,
    defaultLanguage: model.defaultLanguage,
    localizedMetadata: model.localizedMetadata,
    validityPolicy: model.validityPolicy as unknown as DemoRelease['validityPolicy'],
    publicationPolicy: release.publicationPolicy,
    permissions: {
      allowLinkedInProfile: permissions.allowLinkedInProfile === true,
      /* the backend's name for the feed share; the prototype calls it `allowLinkedInShare` */
      allowLinkedInShare: permissions.allowFeedShare === true,
      allowDownload: permissions.allowDownload === true,
    },
    duplicatePolicy: asObject(payload.duplicatePolicy) as unknown as DemoRelease['duplicatePolicy'],
    emailDefaults: model.emailDefaults,
    interoperability: model.interoperability,
    canvas: model.canvas as unknown as DemoRelease['canvas'],
    background: model.background,
    localizedBackground: model.localizedBackground,
    fieldDefinitions: fields,
    layers,
    safeAreas: model.safeAreas,
    linkedInIssuer: model.linkedInIssuer,
    linkedInIssuerUnreadable: storedIssuer.status === 'unreadable',
    renderProfile: asObject(release.renderProfile),
    issuerProfileSnapshot: issuerSnapshot(issuer),
    fontReferences: asArray(payload.fontReferences) as Record<string, unknown>[],
  };
}

export function draftFromApi(detail: TemplateDetail): DemoDraft {
  const draft = detail.draft;
  const model = readTemplateModel(draft?.layout);
  const permissions = asObject(draft?.permissions);
  return {
    credentialType: model.credentialType,
    customTypeLabel: model.customTypeLabel,
    subjectType: model.subjectType,
    holderField: model.holderField,
    defaultLanguage: model.defaultLanguage,
    localizedMetadata: model.localizedMetadata,
    validityPolicy: model.validityPolicy as unknown as DemoDraft['validityPolicy'],
    publicationPolicy: draft?.publicationPolicy ?? 'issue_immediately',
    permissions: {
      allowLinkedInProfile: permissions.allowLinkedInProfile === true,
      allowLinkedInShare: permissions.allowFeedShare === true,
      allowDownload: permissions.allowDownload === true,
    },
    duplicatePolicy: asObject(draft?.duplicatePolicy) as unknown as DemoDraft['duplicatePolicy'],
    emailDefaults: model.emailDefaults,
    interoperability: model.interoperability,
    canvas: model.canvas as unknown as DemoDraft['canvas'],
    background: model.background,
    localizedBackground: model.localizedBackground,
    sealedLanguages: model.sealedLanguages,
    fieldDefinitions: detail.draftFields.map((f) => fieldFromApi(f, model)),
    layers: detail.draftLayers.map((l) => mapLayerFromApi(l, model.canvas as never)),
    safeAreas: model.safeAreas,
    /* The editor's own copy is what the author is typing: a stored block that cannot be read
       comes back empty and is re-entered rather than repaired here, and the settings section
       validates what is typed as it is typed. */
    linkedInIssuer: model.linkedInIssuer,
    renderProfile: asObject(asObject(draft?.layout).renderProfile),
  };
}

/**
 * The two status vocabularies, reconciled.
 *
 * The backend's `template_status` enum is `active | archived` (migration 001) and «publishedness»
 * lives in `currentReleaseId`. The prototype's screens speak `draft | published | archived`, and
 * they mean something by it: a template with no published release cannot issue a credential
 * (rule 3), and the whole product is gated on that word.
 *
 * Passing the backend's value straight through made `status === 'published'` unreachable, so the
 * register believed no template was ever publishable and refused to open its issuance dialog.
 * Derived here, where the two models meet, rather than by loosening the check in every screen.
 */
function templateStatusFromApi(status: string, currentReleaseId: string | null): string {
  if (status === 'archived') return 'archived';
  return currentReleaseId ? 'published' : 'draft';
}

export function templateFromApi(
  detail: TemplateDetail,
  releaseDetails: Map<string, ReleaseDetail>,
  issuer: IssuerProfile | null,
): DemoTemplate {
  const t = detail.template;
  return {
    id: t.id,
    code: t.code,
    name: t.name,
    status: templateStatusFromApi(t.status, t.currentReleaseId),
    currentReleaseId: t.currentReleaseId,
    releases: detail.releases.map((r) =>
      releaseFromApi(r, releaseDetails.get(r.id) ?? null, { code: t.code, name: t.name }, issuer),
    ),
    draft: draftFromApi(detail),
    createdAt: detail.draft?.updatedAt ?? '',
    updatedAt: detail.draft?.updatedAt ?? '',
  };
}

// ---------------------------------------------------------------------------- credentials

/**
 * A credential, from the list row the internal list now returns.
 *
 * Everything the reference's table renders is here. What is NOT here is what only the detail read
 * answers — the claims, the manifest, the revision history, the events, the delivery attempts —
 * and those stay at their empty values until `hydrateCredential` fills them. The screens that need
 * them are the ones that open a single record, and they wait for that read.
 */
export function credentialFromListRow(row: CredentialListRow, release: DemoRelease | null): DemoCredential {
  return {
    id: row.id,
    publicCode: row.publicCode,
    canonicalPublicUrl: row.canonicalPublicUrl,
    templateId: row.templateId,
    releaseId: row.templateReleaseId,
    releaseHash: release?.releaseHash ?? '',
    release: release ?? ({ releaseNumber: row.releaseNumber } as unknown as DemoRelease),
    subjectId: '',
    subjectSnapshot: {
      subjectType: row.subjectType,
      canonicalDisplayName: row.holderDisplayName,
      snapshotHash: row.subjectSnapshotHash,
    },
    claims: {},
    privateContactEmail: null,
    lang: row.lang,
    issueDate: row.issueDate,
    expiresAt: row.expiresAt,
    state: row.state as CredentialState,
    publishedAt: null,
    revokedAt: row.revokedAt,
    supersedesCode: null,
    supersededByCode: null,
    renderManifest: {},
    renderManifestHash: '',
    revision: row.revisionNumber,
    revisions: [],
    claimToken: null,
    deliveryStatus: row.deliveryStatus,
    ...(row.engagement ? { engagement: row.engagement } : {}),
    batchId: row.batchId,
    sourceRow: null,
    createdBy: '',
    createdAt: row.createdAt,
    updatedAt: row.createdAt,
    events: [],
  };
}

export function eventFromApi(e: CredentialEventRecord): DemoEvent {
  return {
    id: e.id,
    credentialId: e.credentialId,
    type: e.type,
    at: e.at,
    actor: e.actor,
    actorType: e.actorType,
    reason: e.reason,
    meta: e.meta ?? {},
    ...(e.correlationId === null ? {} : { correlationId: e.correlationId }),
  };
}

/**
 * The single-record read, merged onto the list row.
 *
 * The list row already carries the identity and the columns; this adds what only the detail
 * answers. `claimToken` deliberately carries no token and no hash — the API does not return
 * either, and a screen that needs the claim URL is told why it cannot have one
 * (`claimUrlStatus.reason`), which is what the reference's own «bağlantı oturumda yok» line says.
 */
export function hydrateCredentialFromApi(
  base: DemoCredential,
  detail: ApiCredentialDetail,
  events: CredentialEventRecord[],
  deliveryStatus: string | null,
): DemoCredential {
  const c = detail.credential;
  const current = detail.revisions.find((r) => r.id === c.currentRevisionId) ?? detail.revisions.at(-1);
  return {
    ...base,
    id: c.id,
    publicCode: c.publicCode,
    canonicalPublicUrl: c.canonicalPublicUrl,
    releaseId: c.templateReleaseId,
    releaseHash: c.releaseHash,
    subjectId: c.subjectId,
    subjectSnapshot: detail.subjectSnapshot
      ? {
          subjectId: c.subjectId,
          subjectType: detail.subjectSnapshot.subjectType,
          canonicalDisplayName: detail.subjectSnapshot.displayName,
          externalReference: detail.subjectSnapshot.externalReference,
          snapshotHash: detail.subjectSnapshot.snapshotHash,
        }
      : base.subjectSnapshot,
    claims: c.claims,
    /* The record's groups, from the read that knows them; `?? []` so a hydrated record says so. */
    groups: detail.groups ?? [],
    privateContactEmail: detail.subject?.privateContactEmail ?? null,
    lang: c.lang,
    issueDate: c.issueDate,
    expiresAt: c.expiresAt,
    state: c.state as CredentialState,
    publishedAt: c.publishedAt,
    revokedAt: c.revokedAt,
    supersedesCode: detail.supersedesPublicCode,
    supersededByCode: detail.supersededByPublicCode,
    renderManifest: detail.manifest?.manifest ?? {},
    renderManifestHash: c.renderManifestHash,
    revision: current?.revisionNumber ?? base.revision,
    /*
     * The values the artwork is DRAWN from, which nothing was carrying.
     *
     * `credentialPlan` reads `renderDataSnapshot`, and the internal hydration never mapped it, so
     * every bound layer came back «değer yok» and the admin screen drew «Görsel doğrulanamadı»
     * about a record whose own public page was showing the real certificate. It belongs to the
     * REVISION — a correction produces a new one — so it is read from the current revision and
     * never from the template.
     */
    renderDataSnapshot: (current?.renderDataSnapshot ?? base.renderDataSnapshot ?? {}) as Record<
      string,
      unknown
    >,
    revisions: detail.revisions,
    claimToken: detail.claimToken
      ? {
          expiresAt: detail.claimToken.expiresAt,
          rotationCount: detail.claimToken.rotationCount,
          consumedAt: detail.claimToken.consumedAt,
          revokedAt: detail.claimToken.revokedAt,
          urlReason: detail.claimUrlStatus.reason,
        }
      : null,
    deliveryStatus: deliveryStatus ?? base.deliveryStatus,
    batchId: c.issuanceBatchId,
    sourceRow: detail.batchOrigin?.rowNo ?? null,
    createdBy: c.createdBy,
    createdAt: c.createdAt,
    updatedAt: c.createdAt,
    events: events.map(eventFromApi),
  };
}

// ---------------------------------------------------------------------------- batches

/**
 * Backend row statuses mapped onto the four numbers the history table prints.
 *
 * The vocabularies differ and the mismatch was silent: the screen counted rows whose status was
 * `'invalid'`, the backend's enum is `pending · planned · issued · needs_resolution ·
 * skipped_duplicate · failed`, and so the «Hatalı» column read 0 for a batch that had rejected half
 * its file. `needs_resolution` is a row the validator refused and `failed` one the run could not
 * issue; both are rejected rows to the person reading the table.
 */
function summaryFromCounts(rowCount: number, s: IssuanceBatchListSummary): BatchSummary {
  const row = (k: string) => s.rowStatus[k] ?? 0;
  const state = (k: string) => s.credentialState[k] ?? 0;
  const mail = (k: string) => s.delivery[k] ?? 0;
  return {
    total: rowCount,
    created: row('issued'),
    invalid: row('needs_resolution') + row('failed'),
    duplicates: row('skipped_duplicate'),
    /* No backend status means «skipped» on its own; a skipped duplicate is counted above. */
    skipped: 0,
    pending: state('pending_acceptance'),
    pendingAdmin: state('pending_admin_publish'),
    active: state('active'),
    expired: state('expired'),
    revoked: state('revoked'),
    superseded: state('superseded'),
    delivered: mail('delivered'),
    bounced: mail('bounced'),
    queued: mail('queued'),
    sent: mail('sent'),
    /*
     * «Gönderilmedi» is `none`, not `disabled`.
     *
     * `disabled` is the reference fixture's own word; `claim_delivery_status` has no such value and
     * never will, so this column counted zero for every batch. The state it meant — no claim e-mail
     * was ever needed — is what the server reports as `none`.
     */
    emailDisabled: mail('none'),
  };
}

/**
 * A rejected row's reasons, as the sentences the screen and the CSV print.
 *
 * The backend stores them structured — field errors with a key and a code, render failures with a
 * code and a place — and the screen reads a list of strings. `Array.isArray` is false for an object,
 * so its defensive mapping quietly produced NO reasons: against the real backend the «Hata» column
 * and «hatalı satırları indir» were both blank about rows that had failed for a stated reason. The
 * code is kept in the text, because it is the part a reader can act on and the part the backend's own
 * report prints.
 */
function errorMessages(errors: unknown): string[] {
  if (errors === null || errors === undefined) return [];
  if (Array.isArray(errors)) return errors.map((e) => String(e));
  if (typeof errors !== 'object') return [String(errors)];
  const e = errors as {
    errors?: { key?: string; code?: string; message?: string }[];
    renderFailures?: { code?: string; where?: string }[];
  };
  const out: string[] = [];
  for (const f of e.errors ?? []) {
    const text = f.message ?? f.code ?? 'geçersiz';
    out.push(f.key ? `${f.key}: ${text}` : text);
  }
  for (const f of e.renderFailures ?? []) {
    const code = f.code ?? 'render_failed';
    out.push(f.where ? `${code} (${f.where})` : code);
  }
  return out;
}

/**
 * One batch row, as the detail screen's three per-row columns need it.
 *
 * The credential's facts ride ON the row — its code, its view state, its delivery state — so the
 * «Sonuç», «E-posta durumu» and «Credential» cells are filled from the answer rather than from
 * whatever the browser had downloaded earlier. `status` stays the server's own word; `batchRowOutcome`
 * is what translates it.
 */
/**
 * The file's columns, in the words the screens read them by.
 *
 * `inputPayload` is keyed by the uploaded file's own HEADERS — «Ad Soyad», «E-posta» — because that
 * is what the server parsed. Every screen asks for a FIELD key (`recipient_name`) or for `email`,
 * so a result table read `data[holderField]`, found nothing, and printed «—» for three rows whose
 * names the register shows correctly. The batch's own `columnMapping` is the translation, and it is
 * the run's own: `claim:<key>` is a claim, and the five issuance inputs have their own names.
 *
 * The header keys are KEPT beside the translated ones — the error CSV and the mapping table read
 * the file's own columns — so this adds names rather than replacing them.
 */
function rowDataWithMapping(
  payload: Record<string, unknown>,
  columnMapping: Record<string, string> | null | undefined,
): Record<string, unknown> {
  if (!columnMapping) return payload;
  const INPUT_KEYS: Record<string, string> = {
    recipientEmail: 'email',
    issueDate: 'issue_date',
    expiresAt: 'expiry_date',
    externalReference: 'external_reference',
    lang: 'lang',
  };
  const out: Record<string, unknown> = { ...payload };
  for (const [header, target] of Object.entries(columnMapping)) {
    const value = payload[header];
    if (value === undefined) continue;
    const key = target.startsWith('claim:') ? target.slice('claim:'.length) : INPUT_KEYS[target];
    if (key) out[key] = value;
  }
  return out;
}

export function batchRowFromApi(
  r: IssuanceRowWithCredential,
  columnMapping: Record<string, string> | null = null,
): DemoBatchRow {
  return {
    n: r.rowNo,
    data: rowDataWithMapping(r.inputPayload, columnMapping),
    status: r.status,
    errors: errorMessages(r.errors),
    warnings: [],
    credentialId: r.credentialId,
    duplicateClass: r.duplicateClass,
    credential: r.credential,
  };
}

export function batchFromApi(
  b: IssuanceBatchRecord,
  rows: IssuanceRowWithCredential[],
  release: DemoRelease | null,
  outcome: string | null,
  /** Server-side counts. Present for every batch now — a list row and the open one alike. */
  counts: IssuanceBatchListSummary | null = null,
  /** Which slice of the rows these are, and how many there are in the batch. */
  window: { total: number; offset: number } | null = null,
): DemoBatch {
  const mapped = rows.map((r) => batchRowFromApi(r, b.columnMapping));
  return {
    ...(counts === null ? {} : { summary: summaryFromCounts(b.rowCount, counts) }),
    /*
     * The page is named as a page. The client holds only what it read, so `rows` carries the same
     * array here — but the screen draws `rowPage`, which is the field that cannot be mistaken for
     * «every row of this batch» when something counts them.
     */
    ...(window === null ? {} : { rowPage: mapped, rowsTotal: window.total, rowsOffset: window.offset }),
    id: b.id,
    filename: b.sourceFilename ?? '',
    /* The operator's own name for this upload (migration 028); null is the filename fallback. */
    name: b.name,
    templateId: release?.templateId ?? '',
    releaseId: b.templateReleaseId,
    releaseHash: release?.releaseHash ?? '',
    status: b.status,
    createdAt: b.createdAt,
    createdBy: b.createdBy,
    /* Both are stored now (`issuance_batches.options`, migration 017), so they are read rather than
       guessed: the run's own language, and whether it was asked to send. */
    lang: (b.options?.lang as 'tr' | 'en' | undefined) ?? release?.defaultLanguage ?? 'tr',
    sendEmail: b.options?.sendEmail === true,
    rows: mapped,
    outcome,
    rowCount: b.rowCount,
    dryRunReport: b.dryRunReport ?? null,
  };
}

// ---------------------------------------------------------------------------- settings

/**
 * The settings screen's own model.
 *
 * `issuerProfile` is real and editable (`issuer.update`). The rest — the e-mail template bodies,
 * the claim validity, the privacy switches, the domain readiness block — has no backend home yet;
 * §7 names the e-mail template read/update as work to do, and the others are recorded as gaps
 * rather than shown as if they were stored. Each one keeps the prototype's own default so the
 * screen renders, and every control that cannot persist says so.
 */
export function settingsFromApi(
  issuer: IssuerProfile | null,
  integration: Awaited<ReturnType<typeof readIntegrationSettings>> | null,
  fallback: DemoSettings,
  emailTemplates: EmailTemplateRecord[] = [],
  /* Whether the issuer READ happened. A refusal is not an absence (PO 7.11 §6). */
  issuerProfileAccess: DemoSettings['issuerProfileAccess'] = 'ok',
): DemoSettings {
  /*
   * The two stored confirmation texts, back in the words the settings tab edits (matrix C1–C4).
   * The tab wrote them and could not read them back, so a save looked like it had been forgotten
   * the moment the screen reloaded.
   */
  const tr = (key: string): EmailTemplateRecord | undefined =>
    emailTemplates.find((t) => t.templateKey === key && t.lang === 'tr');
  const person = tr('claim_invite_v1');
  const org = tr('claim_invite_org_v1');

  return {
    ...fallback,
    email: {
      ...fallback.email,
      ...(person ? { subjectPerson: person.subject, bodyPerson: person.body } : {}),
      ...(org ? { subjectOrg: org.subject, bodyOrg: org.body } : {}),
    },
    issuerProfileId: issuer?.id ?? '',
    issuerProfileAccess,
    issuerDisplayName: issuer?.issuerDisplayName ?? fallback.issuerDisplayName,
    linkedinIssuerName: issuer?.linkedinIssuerName ?? fallback.linkedinIssuerName,
    linkedinPageUrl: issuer?.linkedinPageUrl ?? fallback.linkedinPageUrl,
    /*
     * The verified numeric LinkedIn company id, read out of the issuer's audited organisation
     * reference — never typed into a settings box. Absent means the Add-to-Profile link addresses
     * the organisation by name instead, which prefills the text but does not preselect the Page.
     */
    linkedinOrganizationId: organizationIdFromReference(issuer?.organizationReference ?? null),
    issuerWebsite: issuer?.issuerWebsite ?? fallback.issuerWebsite,
    publicBase: issuer?.publicBase ?? fallback.publicBase,
    supportEmail: issuer?.supportEmail ?? fallback.supportEmail,
    /* what the deployment is actually wired to; the settings screen states it rather than guessing */
    domainReadiness: integration ? { ...fallback.domainReadiness, integration } : fallback.domainReadiness,
  };
}

// ---------------------------------------------------------------------------- the whole graph

export interface LoadOptions {
  /** The prototype's own settings defaults, for the blocks the backend does not store yet. */
  settingsFallback: DemoSettings;
}

/**
 * A read a ROLE may not perform is a section it does not see. Everything else is a failure.
 *
 * The authorization matrix gives different roles different reads: a viewer has `credential.read` and
 * not `import.read`. Letting that one refusal reject the whole projection blanked every screen for
 * that role, so the refusal has to belong to the section it refused.
 *
 * What this used to be was `p.catch(() => fallback)` — every error, not just that one. An expired
 * session, a 500, a 503 and a dropped connection all became an empty collection, and the console
 * then stated as fact that there were no templates, no credentials and no uploads. «The server did
 * not answer» and «there is nothing here» are not the same sentence and must not draw the same
 * screen.
 *
 * So: 403 only, and only where the rest of the projection is still meaningful without it. A 401
 * belongs to the session flow, and a 5xx or a network failure belongs on screen with a retry — both
 * of those reject, and `useApiStore` turns them into the error the shell shows.
 */
const ifRoleAllows = <T>(p: Promise<T>, fallback: T): Promise<T> =>
  p.catch((e: unknown) => {
    if (isApiError(e) && e.status === 403) return fallback;
    throw e;
  });

/**
 * The same rule, but it REPORTS the refusal instead of only surviving it.
 *
 * `ifRoleAllows` turns a 403 into an empty section, which is right for a section a role does not
 * see — and wrong for the issuer profile, where empty is also what a deployment that has none
 * looks like. Those two must not draw the same screen: one of them should be offered «create the
 * first profile» and the other should be told it may not look (PO 7.11 §6).
 */
const readOrRefused = <T>(p: Promise<T>, fallback: T): Promise<{ value: T; refused: boolean }> =>
  p
    .then((value) => ({ value, refused: false }))
    .catch((e: unknown) => {
      if (isApiError(e) && e.status === 403) return { value: fallback, refused: true };
      throw e;
    });

export async function loadDatabase(api: ApiClient, opts: LoadOptions): Promise<DemoDatabase> {
  const [{ templates }, issuerProfiles, integration, emailTemplates] = await Promise.all([
    ifRoleAllows(listTemplates(api), {
      templates: [] as Awaited<ReturnType<typeof listTemplates>>['templates'],
    }),
    /* Reported, not swallowed, so «you may not look» and «there is none» stay different. */
    readOrRefused(readIssuerProfiles(api), { issuerProfiles: [] as IssuerProfile[] }),
    ifRoleAllows(readIntegrationSettings(api), null),
    ifRoleAllows(
      listEmailTemplates(api).then((r) => r.templates),
      [] as EmailTemplateRecord[],
    ),
  ]);
  const issuer = issuerProfiles.value.issuerProfiles[0] ?? null;
  const issuerProfileAccess: DemoSettings['issuerProfileAccess'] = issuerProfiles.refused
    ? 'forbidden'
    : 'ok';

  const details = (
    await Promise.all(templates.map((t) => ifRoleAllows(readTemplate(api, t.id), null)))
  ).filter((d): d is TemplateDetail => d !== null);
  const releaseDetails = new Map<string, ReleaseDetail>();
  await Promise.all(
    details.flatMap((d) =>
      d.releases.map(async (r) => {
        const detail = await ifRoleAllows(readRelease(api, r.id), null);
        if (detail) releaseDetails.set(r.id, detail);
      }),
    ),
  );
  const mappedTemplates = details.map((d) => templateFromApi(d, releaseDetails, issuer));
  const releaseById = new Map<string, DemoRelease>();
  for (const t of mappedTemplates) for (const r of t.releases) releaseById.set(r.id, r);

  /*
   * Neither the register nor the upload history is loaded here any more.
   *
   * They were, and that was the defect: a bounded list became the whole world. The register read one
   * page of two hundred and the screen then counted it, searched it, paged it and looked records up
   * in it, so a deployment with two hundred and five credentials showed 205 as 200, the two hundred
   * and third opened as «Kayıt bulunamadı», and the panel printed the download as the system total.
   * The history read fifty batches AND every row of each of them — twenty-five thousand row payloads
   * to print two hundred integers — and the fifty-first upload was unreachable because nothing said
   * there was one.
   *
   * Both are now read through `V07Queries` (`queries-api.ts`): the server's page, the server's count,
   * and one record fetched by the code that was asked for. The store keeps the collections the
   * console genuinely needs whole — templates with their releases, because a credential renders from
   * its own release — and states emptiness here rather than implying completeness.
   */
  return {
    templates: mappedTemplates,
    credentials: [],
    batches: [],
    /*
     * GAP: there is no subject LIST endpoint, only an advisory search (`GET /subjects?q=…`, which
     * BACKEND §8 defines as advisory and which never merges identities). §6 puts recipient facts
     * inside the credential flows rather than in a register of their own, so nothing reads this.
     */
    subjects: [],
    /*
     * The global audit ledger is read per credential, in the credential detail's own timeline —
     * which is where §6 puts it. `GET /audit` still exists for a correlation-id lookup.
     */
    eventLog: [],
    deliveries: [],
    settings: settingsFromApi(
      issuer,
      integration,
      opts.settingsFallback,
      emailTemplates,
      issuerProfileAccess,
    ),
  };
}

/** Fills in everything only the single-record read answers. */
export async function hydrateCredential(
  api: ApiClient,
  base: DemoCredential,
  credentialId: string,
): Promise<{ credential: DemoCredential; deliveries: DemoDelivery[] }> {
  const [detail, events, delivery] = await Promise.all([
    readCredential(api, credentialId),
    readCredentialEvents(api, credentialId).then((r) => r.events),
    readCredentialDeliveries(api, credentialId).catch(() => ({ attempts: [], deliveries: [] })),
  ]);
  /*
   * The record's e-mail state, by the SAME rule the server's projection uses — the most recent
   * send, claim attempt or notification alike (`latestDeliveryStatus`). This line used to be
   * `delivery.attempts.at(-1)`, the latest CLAIM attempt, and a certificate published directly has
   * none: its notification was delivered while this badge said «Gönderilmedi». It is this value
   * the screen shows, because the hydrated copy is the richer one and wins over the row read by
   * code, so fixing the server's half alone would have left the operator reading the old answer.
   */
  const shownDelivery = latestDeliveryStatus(delivery);
  /*
   * The bytes the manifest was hashed over, for the internal screen too (rule 10).
   *
   * The same artifact the public page shows, from the record's own route, for the record's CURRENT
   * revision — no new permission and no new route: the request is the anonymous one the visitor
   * makes, so it serves exactly what this record's own rules already serve and nothing more. A
   * record whose rules withhold it (a waiting one, rule 12) answers 404 and the screen falls back
   * to drawing the design from the revision's render data, which is what the reference does.
   */
  const artifactSvg = await getPublicArtifactSvg(api, base.publicCode).catch(() => null);
  const hydrated = hydrateCredentialFromApi(base, detail, events, shownDelivery);
  return {
    credential: artifactSvg === null ? hydrated : { ...hydrated, artifactSvg },
    deliveries: delivery.deliveries.map((d) => ({
      id: d.id,
      credentialId: d.credentialId ?? credentialId,
      recipientEmail: d.toEmail,
      state: d.status,
      createdAt: d.queuedAt,
      queuedAt: d.queuedAt,
      sentAt: d.sentAt,
      deliveredAt: d.deliveredAt,
      bouncedAt: null,
      failedAt: d.failedAt,
      failureCode: d.errorCode,
      purpose: d.purpose,
    })),
  };
}
